<template>
  <ui-popover :open="isOpen" @update:open="(value) => isOpen = value">
    <ui-popover-trigger as-child>
      <button
        type="button"
        data-slot="date-range-filter"
        :disabled="disabled"
        class="glass-input flex h-9 w-full items-center justify-between gap-2 rounded-md px-3 text-sm whitespace-nowrap focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-56"
      >
        <span class="flex items-center gap-2">
          <calendar-icon class="size-4 text-muted-foreground" />
          {{ selectedLabel }}
        </span>
        <chevron-down-icon class="size-4 text-muted-foreground" />
      </button>
    </ui-popover-trigger>

    <ui-popover-content align="end" class="w-auto p-3">
      <ui-radio-group
        :model-value="selectedOption"
        class="flex flex-col gap-2.5"
        @update:model-value="(value) => selectOption(value)"
      >
        <label
          v-for="(label, key) in options"
          :key="'date-range-option-' + key"
          :for="'date-range-option-' + key"
          class="flex items-center gap-2 text-sm"
        >
          <ui-radio-group-item :id="'date-range-option-' + key" :value="key" />
          {{ label }}
        </label>
      </ui-radio-group>

      <ui-date-range-calendar
        v-if="selectedOption === OPTION_CUSTOM_RANGE"
        :model-value="modelValue"
        :locale="$root.language"
        :max-date="today"
        class="mt-2 -mx-3 -mb-3"
        @update:model-value="(value) => $emit('update:modelValue', value)"
      />
    </ui-popover-content>
  </ui-popover>
</template>

<script>
import { Calendar, ChevronDown } from '@lucide/vue';

import { formatDateRange } from '@/utils/date';

const OPTION_LAST_7_DAYS = 'last7';
const OPTION_LAST_30_DAYS = 'last30';
const OPTION_CUSTOM_RANGE = 'customRange';

// The presets cover the days up to yesterday. Calendar-day arithmetic (not
// milliseconds) keeps the start at midnight across daylight saving changes.
function presetRange(numberOfDays) {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  end.setMilliseconds(-1);

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - numberOfDays);

  return { start, end };
}

function sameRange(a, b) {
  return a.start.getTime() === b.start.getTime() && a.end.getTime() === b.end.getTime();
}

/** Date range picker with "last 7 / 30 days" presets and a custom range calendar. */
export default {
  name: 'DateRangeFilter',
  components: {
    CalendarIcon: Calendar,
    ChevronDownIcon: ChevronDown,
  },
  props: {
    // { start: Date, end: Date }
    modelValue: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  emits: [ 'update:modelValue' ],
  data() {
    return {
      OPTION_CUSTOM_RANGE,
      isOpen: false,
      customSelected: false,
      today: new Date(),
      options: {
        [OPTION_LAST_7_DAYS]: this.tr('Last 7 days'),
        [OPTION_LAST_30_DAYS]: this.tr('Last 30 days'),
        [OPTION_CUSTOM_RANGE]: this.tr('Custom range'),
      },
    };
  },
  computed: {
    selectedOption() {
      if (this.customSelected) {
        return OPTION_CUSTOM_RANGE;
      }
      if (sameRange(this.modelValue, presetRange(7))) {
        return OPTION_LAST_7_DAYS;
      }
      if (sameRange(this.modelValue, presetRange(30))) {
        return OPTION_LAST_30_DAYS;
      }

      return OPTION_CUSTOM_RANGE;
    },
    selectedLabel() {
      if (this.selectedOption === OPTION_CUSTOM_RANGE) {
        return formatDateRange(this.modelValue.start, this.modelValue.end, this.tr);
      }

      return this.options[this.selectedOption];
    },
  },
  methods: {
    selectOption(option) {
      this.customSelected = option === OPTION_CUSTOM_RANGE;
      if (this.customSelected) {
        return;
      }

      const range = presetRange(option === OPTION_LAST_30_DAYS ? 30 : 7);
      if (!sameRange(this.modelValue, range)) {
        this.$emit('update:modelValue', range);
      }
      this.isOpen = false;
    },
  },
};
</script>
